import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { isSidebarNavItemActive } from './sidebarNavActive';
import {
  sidebarCollapsedTooltip,
  sidebarNavIcon,
  sidebarNavItem,
} from './sidebarNavTokens';

export interface SidebarNavItemConfig {
  name: string;
  href: string;
  icon: LucideIcon;
}

interface SidebarNavItemProps {
  item: SidebarNavItemConfig;
  collapsed?: boolean;
  variant: 'desktop' | 'mobile';
  onNavigate?: () => void;
  onMouseEnter?: () => void;
}

export const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  item,
  collapsed = false,
  variant,
  onNavigate,
  onMouseEnter,
}) => {
  const location = useLocation();
  const Icon = item.icon;
  const isDesktop = variant === 'desktop';
  const isCollapsedDesktop = isDesktop && collapsed;

  return (
    <NavLink
      to={item.href}
      onClick={onNavigate}
      onMouseEnter={onMouseEnter}
      title={isCollapsedDesktop ? item.name : undefined}
      className={({ isActive }) => {
        const active = isSidebarNavItemActive(item.name, isActive, location.pathname);
        return cn(
          sidebarNavItem.base,
          isDesktop
            ? isCollapsedDesktop
              ? sidebarNavItem.spacingCollapsed
              : sidebarNavItem.spacingExpanded
            : sidebarNavItem.spacingMobile,
          active ? sidebarNavItem.active : sidebarNavItem.inactive,
          sidebarNavItem.focus
        );
      }}
    >
      {({ isActive }) => {
        const active = isSidebarNavItemActive(item.name, isActive, location.pathname);

        return (
          <>
            <Icon
              className={cn(
                sidebarNavIcon.base,
                isDesktop ? sidebarNavIcon.sizeExpanded : sidebarNavIcon.sizeMobile,
                isCollapsedDesktop && 'mx-auto',
                active ? sidebarNavIcon.active : sidebarNavIcon.inactive
              )}
              strokeWidth={2}
              aria-hidden
            />
            {(!isDesktop || !collapsed) && (
              <span
                className={cn(
                  'min-w-0 flex-1 truncate transition-colors duration-200',
                  active ? sidebarNavItem.activeLabel : 'text-inherit'
                )}
              >
                {item.name}
              </span>
            )}
            {isCollapsedDesktop && (
              <span className={sidebarCollapsedTooltip} role="tooltip">
                {item.name}
              </span>
            )}
          </>
        );
      }}
    </NavLink>
  );
};
